import { dayMonth, hour } from '@app/format';
import { t } from '@app/i18n';
import type { RuntimeStatus } from './Runtime.api';

/**
 * As horas da prova do «Identidade válida» em T15 (aim-ivi.1, protótipo §1.1): «renovado às 21:16 · usado no gateway
 * às 21:40». A renovação vem do log do helper de identidade, e a chamada vem das decisões guardadas.
 *
 * Sem chamada no histórico, a linha não diz nada sobre o gateway (decisão 3 do mrolim): a chamada é o reforço da prova,
 * e um ambiente parado não chama nada. Sem as duas horas, a linha fica com o «ok» de sempre.
 */
export interface ProofPart {
  /** `renewed` ou `seen`: o `data-testid` da parte, para os testes afirmarem a estrutura. */
  kind: 'renewed' | 'seen';
  at: string;
  text: string;
}

export function identityValidProof(
  status: Pick<RuntimeStatus, 'renewed_at' | 'seen_at_gateway'>,
  now: Date = new Date(),
): ProofPart[] {
  const parts: ProofPart[] = [];
  if (status.renewed_at) {
    parts.push({ kind: 'renewed', at: status.renewed_at, text: atOrOn(status.renewed_at, 'renewed', now) });
  }
  if (status.seen_at_gateway) {
    parts.push({ kind: 'seen', at: status.seen_at_gateway, text: atOrOn(status.seen_at_gateway, 'used', now) });
  }
  return parts;
}

/** «às 14:05» no mesmo dia, «em 09/10 14:05» em outro, como o «lido às» da foto do runtime (aim-6ph.13, decisão 7). */
function atOrOn(iso: string, form: 'renewed' | 'used', now: Date): string {
  const time = hour(iso) ?? '';
  if (sameDay(new Date(iso), now)) return t(`runtimeStatus.check.identity_valid.${form}At`, { time });
  return t(`runtimeStatus.check.identity_valid.${form}On`, { date: dayMonth(iso) ?? '', time });
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}
